import { Device, UpdateDeviceConfigGeoAlertRequest } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseUpdateDeviceGeoAlertActionProps = {
    deviceId: string;
};

/**
 * Everything the caller chooses per call. The device is fixed for the hook's
 * lifetime; the slot is not, because one screen writes all four.
 */
export type UpdateDeviceGeoAlertVariables = {
    /** Which of the device's four geofence slots to write (0-3). */
    slot: number;
    data: UpdateDeviceConfigGeoAlertRequest;
};

type UseUpdateDeviceGeoAlertActionData = {
    updateDeviceGeoAlert: (
        variables: UpdateDeviceGeoAlertVariables
    ) => Promise<Device>;
    loading: boolean;
    error: string;
};

/**
 * Writing one of a device's four geofence slots: its shape, which way it
 * alerts, and whether it is on at all.
 *
 * No `isAllowed`/`reason`: the gate is the device settings page's own
 * `canEdit`, which it works out once for every action on the device and passes
 * down to the buttons. Whether a *feature* can be turned on is a separate
 * question, answered by `useDeviceAlertLicenses`.
 */
export function useUpdateDeviceGeoAlertAction(
    props: UseUpdateDeviceGeoAlertActionProps
): UseUpdateDeviceGeoAlertActionData {
    const { deviceId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `PATCH /v3/devices/{deviceId}/config/geofence/{slot}` declares gets
    // a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'DEVICE_NOT_SETUP')) {
                return t('shared.errors.device_not_setup');
            }
            if (isApiError(error, 'NOT_SUBSCRIBED')) {
                return t('shared.errors.not_subscribed');
            }
            if (isApiError(error, 'NO_MORE_LICENSES')) {
                return t('shared.errors.no_more_licenses');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (variables: UpdateDeviceGeoAlertVariables) =>
            openApiRef.current.devices
                .updateDeviceConfigGeoAlert(
                    deviceId,
                    String(variables.slot),
                    variables.data
                )
                .then((response) => response.data.data.device),
        onSuccess: (device) => {
            // The device downloads the new configuration in its own time, so
            // what comes back carries the change as pending. Write it: the
            // lists have to show that it is on its way.
            addOrUpdateAllDevicesCache(queryClient, device);

            UIToast.success(t('actions.update_geo_alert.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        updateDeviceGeoAlert: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
